import { inject, Injectable } from '@angular/core';
import { FormArray, FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SkillType } from '@digilize/shared/definitions/src';

import { dateAfterOtherCustomErrorValidator } from '../validators/date-after-custom-error.validators';
import { dateBeforeOtherCustomErrorValidator } from '../validators/date-before-custom-error.validators';

@Injectable({ providedIn: 'root' })
export class CvFormHelperService {
  private fb = inject(FormBuilder);

  skillsToFormArray(data, prop: string, formArray: FormArray, clearFormArr?: boolean): void {
    if (clearFormArr) {
      formArray.clear();
    }
    if (!data[prop]) {
      return;
    }
    data[prop].forEach((item: any) => {
      if (this.hasSkill(formArray, item.skill_id)) {
        return;
      }
      formArray.push(
        this.fb.group({
          id: item.id,
          skill_id: item.skill_id,
          skill_name: item.skill_name,
          rating: item.rating,
          type: item.type,
        })
      );
    });
  }

  languagesToFormArray(data, prop: string, formArray: FormArray, clearFormArr?: boolean): void {
    if (clearFormArr) {
      formArray.clear();
    }
    if (!data[prop]) {
      return;
    }
    data[prop].forEach((item: any) => {
      formArray.push(
        this.fb.group({
          id: item.id,
          skill_id: [item.skill_id],
          skill_name: [item.skill_name],
          val: item.val,
          rating: item.rating,
          rating_written: item.rating_written ?? 0,
          is_native: item.is_native,
        })
      );
    });
  }

  educationToFormArray(data, prop: string, formArray: FormArray): void {
    if (!data[prop]) {
      return;
    }
    data[prop].forEach((item: any) => {
      formArray.push(
        this.fb.group({
          id: item.id,
          order: item.order,
          country: item.country,
          school_name: item.school_name,
          education_name: item.education_name,
          education_level: item.education_level,
          start_date: item.start_date,
          end_date: item.end_date,
          is_current: item.is_current,
        })
      );
    });
  }

  addWorkHistoryData(data, workHistoryArray: FormArray, withValidators = false): void {
    if (!data.work_history) {
      return;
    }
    data.work_history.forEach((item: any) => {
      const startDate = item.start_date === '1970-01-01T00:00:00+00:00' || item.start_date === null ? null : item.start_date;
      const endDate = item.end_date === '1970-01-01T00:00:00+00:00' || item.end_date === null ? '' : item.end_date;

      const group = withValidators
        ? this.fb.group({
            position_id: item.position_id,
            position_name: [item.position_name],
            position_name_original: [item.position_name_original, [Validators.required]],
            company_name: [item.company_name],
            job_description: item.job_description,
            order: item.order,
            start_date: [startDate, dateBeforeOtherCustomErrorValidator('end_date', 'startDate')],
            end_date: [endDate, dateAfterOtherCustomErrorValidator('start_date', 'endDate')],
            is_current: [item.is_current],
          })
        : this.fb.group({
            position_id: item.position_id,
            position_name: [item.position_name],
            position_name_original: [item.position_name_original],
            company_name: [item.company_name],
            job_description: item.job_description,
            order: item.order,
            start_date: [startDate],
            end_date: [endDate],
            is_current: [item.is_current],
          });

      workHistoryArray.push(group);
    });
  }

  patchUserPhoto(form: UntypedFormGroup, data): void {
    form.patchValue({
      photo: data.photo,
      photo_id: data.photo_id,
    });
  }

  populateFormArraysFromCv(
    data,
    arrays: {
      softSkills: FormArray;
      knowledgeSkills: FormArray;
      profSkills: FormArray;
      taskSkills: FormArray;
      itSkills: FormArray;
      education: FormArray;
      languages: FormArray;
      workHistory: FormArray;
    },
    withValidators = false
  ): void {
    this.skillsToFormArray(data, 'soft_skills', arrays.softSkills);
    this.skillsToFormArray(data, 'knowledge_skills', arrays.knowledgeSkills);
    this.appendSkillsByType(data.skills, arrays.knowledgeSkills, SkillType.Skills);
    this.skillsToFormArray(data, 'task_skills', arrays.taskSkills);
    this.skillsToFormArray(data, 'it_skills', arrays.itSkills);
    this.educationToFormArray(data, 'education', arrays.education);
    this.languagesToFormArray(data, 'language_skills', arrays.languages);
    this.addWorkHistoryData(data, arrays.workHistory, withValidators);
  }

  refreshSkillData(data, skillsName: string, formArray: FormArray): void {
    if (skillsName === 'knowledge_skills') {
      this.skillsToFormArray(data, 'knowledge_skills', formArray, true);
      this.appendSkillsByType(data.skills, formArray, SkillType.Skills);
    } else if (skillsName === 'languages') {
      this.languagesToFormArray(data, 'language_skills', formArray, true);
    } else {
      this.skillsToFormArray(data, skillsName, formArray, true);
    }
  }

  private appendSkillsByType(items: any[], formArray: FormArray, type: SkillType): void {
    if (!items) return;
    items
      .filter((item) => item.type === type)
      .forEach((item) => {
        if (this.hasSkill(formArray, item.skill_id)) {
          return;
        }
        formArray.push(
          this.fb.group({
            id: item.id,
            skill_id: item.skill_id,
            skill_name: item.skill_name,
            rating: item.rating,
            type: item.type,
          })
        );
      });
  }

  private hasSkill(formArray: FormArray, skillId: string | number | null | undefined): boolean {
    if (skillId === null || skillId === undefined) return false;
    return formArray.controls.some((control) => control.get('skill_id')?.value === skillId);
  }
}
